import { useEffect, useState, type FormEvent } from 'react';
import { useAdminEvent } from '@/app/admin/AdminEventContext';
import { adminApi } from '@/features/admin/api/adminApi';
import { Button } from '@/shared/components/Button';
import { ExportButton } from '@/shared/components/ExportButton';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { Pill } from '@/shared/components/Pill';
import { ApiError } from '@/shared/api/httpClient';
import type { DelegateDto, RegistrationType } from '@/shared/types/domain';

const REGISTRATION_TYPES: RegistrationType[] = ['Visitor', 'Delegate', 'Speaker', 'Press', 'Vip', 'Staff'];

export function DelegatesPage() {
  const { eventId, isLoading: isEventLoading } = useAdminEvent();
  const [delegates, setDelegates] = useState<DelegateDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  function refetch() {
    if (!eventId) return;
    setIsLoading(true);
    adminApi.delegates.listByEvent(eventId).then(setDelegates).finally(() => setIsLoading(false));
  }

  useEffect(refetch, [eventId]);

  async function handleExport(format: 'csv' | 'xlsx') {
    if (!eventId) return;
    setExportError(null);
    try {
      await (format === 'csv' ? adminApi.delegates.exportCsv(eventId) : adminApi.delegates.exportXlsx(eventId));
    } catch (err) {
      setExportError(err instanceof ApiError ? err.userMessage : `Could not export ${format.toUpperCase()}.`);
    }
  }

  if (isEventLoading) return <Spinner label="Loading" />;
  if (!eventId) return <EmptyState icon="◧" title="No events yet" description="Create an event first." />;

  return (
    <div>
      <div className="admin-page__header">
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
          {delegates.length} delegate{delegates.length === 1 ? '' : 's'} registered
        </p>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
          <ExportButton onExport={handleExport} disabled={delegates.length === 0} label="Export (for printer)" />
          <Button onClick={() => setShowCreate((v) => !v)}>{showCreate ? 'Cancel' : '+ Register delegate'}</Button>
        </div>
      </div>

      {exportError && <div className="error-banner" style={{ marginBottom: 'var(--space-4)' }}>{exportError}</div>}

      {showCreate && (
        <CreateDelegateForm
          eventId={eventId}
          onCreated={() => {
            setShowCreate(false);
            refetch();
          }}
        />
      )}

      {isLoading ? (
        <Spinner label="Loading delegates" />
      ) : delegates.length === 0 ? (
        <EmptyState icon="☰" title="No delegates registered" description="Register the first delegate above." />
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Company</th>
              <th>Badge ID</th>
              <th>Type</th>
              <th>Consent</th>
            </tr>
          </thead>
          <tbody>
            {delegates.map((d) => (
              <>
                <tr key={d.id} style={{ cursor: 'pointer' }} onClick={() => setEditingId(editingId === d.id ? null : d.id)}>
                  <td>
                    {d.firstName} {d.lastName}
                  </td>
                  <td>{d.company}</td>
                  <td className="font-mono">{d.badgeId}</td>
                  <td>{d.registrationType}</td>
                  <td>
                    <Pill tone={d.consentGiven ? 'online' : 'offline'}>{d.consentGiven ? 'Given' : 'Not given'}</Pill>
                  </td>
                </tr>
                {editingId === d.id && (
                  <tr key={`${d.id}-edit`}>
                    <td colSpan={5} style={{ background: 'var(--bg)' }}>
                      <EditDelegateForm
                        delegate={d}
                        onSaved={() => {
                          setEditingId(null);
                          refetch();
                        }}
                      />
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CreateDelegateForm({ eventId, onCreated }: { eventId: string; onCreated: () => void }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [email, setEmail] = useState('');
  const [badgeId, setBadgeId] = useState('');
  const [registrationType, setRegistrationType] = useState<RegistrationType>('Visitor');
  const [consentGiven, setConsentGiven] = useState(true);
  const [qrScheme, setQrScheme] = useState<'Uuid' | 'Url'>('Uuid');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [created, setCreated] = useState<DelegateDto | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      const result = await adminApi.delegates.create({
        eventId,
        badgeId,
        firstName,
        lastName,
        company,
        jobTitle,
        email,
        registrationType,
        consentGiven,
        qrScheme
      });
      setCreated(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not register delegate.');
    } finally {
      setIsSaving(false);
    }
  }

  if (created) {
    return (
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-5)',
          marginBottom: 'var(--space-5)'
        }}
      >
        <p style={{ margin: '0 0 8px' }}>
          ✓ Registered <strong>{created.firstName} {created.lastName}</strong>. QR identifier (encode this into their badge):
        </p>
        <p className="font-mono" style={{ wordBreak: 'break-all', color: 'var(--accent)' }}>
          {created.qrIdentifier}
        </p>
        <Button
          variant="secondary"
          onClick={() => {
            setCreated(null);
            onCreated();
          }}
        >
          Register another
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--space-4)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-5)',
        marginBottom: 'var(--space-5)'
      }}
    >
      {error && <div className="error-banner" style={{ gridColumn: '1 / -1' }}>{error}</div>}

      <div className="field">
        <label className="field__label">First name</label>
        <input className="field__input" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Last name</label>
        <input className="field__input" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Company</label>
        <input className="field__input" value={company} onChange={(e) => setCompany(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Job title</label>
        <input className="field__input" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Email</label>
        <input type="email" className="field__input" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Badge ID</label>
        <input className="field__input" required value={badgeId} onChange={(e) => setBadgeId(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Registration type</label>
        <select
          className="field__input"
          value={registrationType}
          onChange={(e) => setRegistrationType(e.target.value as RegistrationType)}
        >
          {REGISTRATION_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label className="field__label">QR scheme</label>
        <select className="field__input" value={qrScheme} onChange={(e) => setQrScheme(e.target.value as 'Uuid' | 'Url')}>
          <option value="Uuid">Random opaque UUID</option>
          <option value="Url">URL (scan.company.com/badgeId)</option>
        </select>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', gridColumn: '1 / -1' }}>
        <input type="checkbox" checked={consentGiven} onChange={(e) => setConsentGiven(e.target.checked)} />
        Delegate has given consent to share their details with exhibitors
      </label>

      <div style={{ gridColumn: '1 / -1' }}>
        <Button type="submit" isLoading={isSaving}>
          Register delegate
        </Button>
      </div>
    </form>
  );
}

function EditDelegateForm({ delegate, onSaved }: { delegate: DelegateDto; onSaved: () => void }) {
  const [badgeId, setBadgeId] = useState(delegate.badgeId);
  const [firstName, setFirstName] = useState(delegate.firstName);
  const [lastName, setLastName] = useState(delegate.lastName);
  const [company, setCompany] = useState(delegate.company);
  const [jobTitle, setJobTitle] = useState(delegate.jobTitle);
  const [email, setEmail] = useState(delegate.email);
  const [phone, setPhone] = useState(delegate.phone ?? '');
  const [registrationType, setRegistrationType] = useState<RegistrationType>(delegate.registrationType as RegistrationType);
  const [consentGiven, setConsentGiven] = useState(delegate.consentGiven);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await adminApi.delegates.update(delegate.id, {
        badgeId,
        firstName,
        lastName,
        company,
        jobTitle,
        email,
        phone: phone || null,
        registrationType,
        consentGiven
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Could not save changes.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 'var(--space-4)',
        padding: 'var(--space-5)'
      }}
    >
      {error && <div className="error-banner" style={{ gridColumn: '1 / -1' }}>{error}</div>}

      <div className="field" style={{ gridColumn: '1 / -1' }}>
        <label className="field__label">QR identifier (fixed — already printed on this badge)</label>
        <input className="field__input font-mono" value={delegate.qrIdentifier} disabled style={{ opacity: 0.6 }} />
      </div>

      <div className="field">
        <label className="field__label">First name</label>
        <input className="field__input" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Last name</label>
        <input className="field__input" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Company</label>
        <input className="field__input" value={company} onChange={(e) => setCompany(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Job title</label>
        <input className="field__input" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Email</label>
        <input type="email" className="field__input" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Phone</label>
        <input className="field__input" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Badge ID</label>
        <input className="field__input" required value={badgeId} onChange={(e) => setBadgeId(e.target.value)} />
      </div>
      <div className="field">
        <label className="field__label">Registration type</label>
        <select
          className="field__input"
          value={registrationType}
          onChange={(e) => setRegistrationType(e.target.value as RegistrationType)}
        >
          {REGISTRATION_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', gridColumn: '1 / -1' }}>
        <input type="checkbox" checked={consentGiven} onChange={(e) => setConsentGiven(e.target.checked)} />
        Delegate has given consent to share their details with exhibitors
      </label>

      <div style={{ gridColumn: '1 / -1' }}>
        <Button type="submit" isLoading={isSaving}>
          Save changes
        </Button>
      </div>
    </form>
  );
}